import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Download,
  Calendar,
  User,
  Tag,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  History,
  ExternalLink,
} from 'lucide-react';
import { format, differenceInDays, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  type LegalDocument,
  documentTypeLabels,
  documentStatusLabels,
  documentStatusColors,
  confidentialityLabels,
  confidentialityColors,
} from '@/schemas/legalDocumentSchemas';
import { useLegalDocumentAuditLog } from '@/hooks/useLegalDocuments';

interface DocumentViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: LegalDocument | null;
}

export function DocumentViewerDialog({
  open,
  onOpenChange,
  document,
}: DocumentViewerDialogProps) {
  const { data: auditLog = [] } = useLegalDocumentAuditLog(document?.id || null);

  if (!document) return null;

  const daysUntilExpiration = document.expiration_date
    ? differenceInDays(new Date(document.expiration_date), new Date())
    : null;

  const isExpired = document.expiration_date && isPast(new Date(document.expiration_date));

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getAuditActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      created: 'Creado',
      updated: 'Actualizado',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      viewed: 'Visualizado',
      downloaded: 'Descargado',
      shared: 'Compartido',
      archived: 'Archivado',
      restored: 'Restaurado',
      deleted: 'Eliminado',
      version_created: 'Nueva versión',
    };
    return labels[action] || action;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold">{document.title}</h2>
              {document.code && (
                <p className="text-sm text-muted-foreground">{document.code}</p>
              )}
            </div>
            <Badge className={cn(documentStatusColors[document.status])}>
              {documentStatusLabels[document.status]}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details" className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Detalles</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
            <TabsTrigger value="preview">Vista Previa</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[50vh] mt-4">
            <TabsContent value="details" className="space-y-6 pr-4">
              {/* Información Principal */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Tipo de Documento</p>
                  <p className="font-medium">{documentTypeLabels[document.document_type]}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Categoría</p>
                  <p className="font-medium">{document.category?.name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Versión</p>
                  <p className="font-medium">{document.version}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Confidencialidad</p>
                  <Badge className={cn(confidentialityColors[document.confidentiality_level])}>
                    <Shield className="w-3 h-3 mr-1" />
                    {confidentialityLabels[document.confidentiality_level]}
                  </Badge>
                </div>
              </div>

              <Separator />

              {/* Descripción */}
              {document.description && (
                <>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Descripción</p>
                    <p className="text-sm text-muted-foreground">{document.description}</p>
                  </div>
                  <Separator />
                </>
              )}

              {/* Fechas */}
              <div className="space-y-4">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Fechas Importantes
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Vigencia desde</p>
                    <p className="font-medium">
                      {document.effective_date
                        ? format(new Date(document.effective_date), 'dd MMM yyyy', { locale: es })
                        : '-'}
                    </p>
                  </div>
                  <div className={cn(
                    "p-3 rounded-lg",
                    isExpired ? "bg-red-50 border border-red-200" :
                    daysUntilExpiration !== null && daysUntilExpiration <= 30
                      ? "bg-yellow-50 border border-yellow-200"
                      : "bg-muted/50"
                  )}>
                    <p className="text-xs text-muted-foreground">Vencimiento</p>
                    <p className="font-medium flex items-center gap-2">
                      {document.expiration_date
                        ? format(new Date(document.expiration_date), 'dd MMM yyyy', { locale: es })
                        : '-'}
                      {isExpired && <AlertTriangle className="w-4 h-4 text-red-500" />}
                      {!isExpired && daysUntilExpiration !== null && daysUntilExpiration <= 30 && (
                        <Clock className="w-4 h-4 text-yellow-600" />
                      )}
                    </p>
                    {daysUntilExpiration !== null && (
                      <p className={cn(
                        "text-xs mt-1",
                        isExpired ? "text-red-600" :
                        daysUntilExpiration <= 30 ? "text-yellow-600" : "text-muted-foreground"
                      )}>
                        {isExpired
                          ? `Vencido hace ${Math.abs(daysUntilExpiration)} días`
                          : `Vence en ${daysUntilExpiration} días`}
                      </p>
                    )}
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Próxima revisión</p>
                    <p className="font-medium">
                      {document.review_date
                        ? format(new Date(document.review_date), 'dd MMM yyyy', { locale: es })
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Archivo */}
              {document.file_name && (
                <>
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Archivo Adjunto</p>
                    <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-primary" />
                        <div>
                          <p className="font-medium">{document.file_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatFileSize(document.file_size)} • {document.file_type || 'Desconocido'}
                          </p>
                        </div>
                      </div>
                      {document.file_url && (
                        <Button asChild>
                          <a href={document.file_url} target="_blank" rel="noopener noreferrer">
                            <Download className="w-4 h-4 mr-2" />
                            Descargar
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Información Adicional */}
              <div className="space-y-4">
                <p className="text-sm font-medium">Información Adicional</p>
                <div className="grid grid-cols-2 gap-4">
                  {document.issued_by && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Emitido por</p>
                      <p className="font-medium">{document.issued_by}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Creado</p>
                    <p className="font-medium">
                      {format(new Date(document.created_at), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Última modificación</p>
                    <p className="font-medium">
                      {format(new Date(document.updated_at), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Etiquetas */}
              {document.tags && document.tags.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Etiquetas
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {document.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Notas */}
              {document.notes && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Notas</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {document.notes}
                    </p>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="history" className="pr-4">
              <div className="space-y-4">
                <p className="text-sm font-medium flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Historial de Actividad
                </p>
                {auditLog.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto opacity-50 mb-3" />
                    <p>No hay actividad registrada</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditLog.map((log) => (
                      <div
                        key={log.id}
                        className="flex items-start gap-3 p-3 rounded-lg border"
                      >
                        <div className="p-2 rounded-full bg-muted">
                          {log.action === 'created' && <FileText className="w-4 h-4" />}
                          {log.action === 'updated' && <Clock className="w-4 h-4" />}
                          {log.action === 'approved' && <CheckCircle className="w-4 h-4 text-green-500" />}
                          {log.action === 'downloaded' && <Download className="w-4 h-4" />}
                          {!['created', 'updated', 'approved', 'downloaded'].includes(log.action) && (
                            <History className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{getAuditActionLabel(log.action)}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(log.performed_at), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="preview" className="pr-4">
              <div className="flex flex-col items-center justify-center py-12">
                {document.file_url ? (
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">
                      Vista previa disponible para archivos PDF
                    </p>
                    <Button asChild>
                      <a href={document.file_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Abrir en nueva pestaña
                      </a>
                    </Button>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <FileText className="w-16 h-16 mx-auto opacity-50 mb-4" />
                    <p>No hay archivo adjunto</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
